import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { Helmet } from 'react-helmet-async';
import OptimizedImage from '../common/OptimizedImage';

const NotFound = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const quickActions = [
    {
      title: t('notFound.goHome'),
      description: t('notFound.goHomeDesc'),
      icon: <HomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      action: () => navigate('/'),
      color: 'primary'
    },
    {
      title: t('notFound.searchSalons'),
      description: t('notFound.searchSalonsDesc'),
      icon: <SearchIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      action: () => navigate('/?search=true'),
      color: 'secondary'
    },
    {
      title: t('notFound.getSupport'),
      description: t('notFound.getSupportDesc'),
      icon: <SupportIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      action: () => navigate('/support'),
      color: 'info'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('notFound.pageTitle')} | BookingBarber</title>
        <meta name="description" content={t('notFound.metaDescription')} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box>
        <AppBar position="static" sx={{ backgroundColor: 'white', color: 'black', boxShadow: 1 }}>
          <Toolbar>
            <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box display="flex" alignItems="center">
              <OptimizedImage
                src="/Logos/logoFocus.png"
                alt="BookingBarber"
                width={32}
                height={32}
                priority={true}
                sx={{ marginRight: 1.5 }}
              />
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                BookingBarber
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 8 }}>
          {/* Hero Section */}
          <Box textAlign="center" mb={8}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '4rem', md: '8rem' },
                fontWeight: 'bold',
                color: 'primary.main',
                mb: 2,
                lineHeight: 1
              }}
            >
              404
            </Typography>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              {t('notFound.title')}
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
            >
              {t('notFound.subtitle')}
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              sx={{ 
                px: 4, 
                py: 1.5,
                fontSize: '1.1rem',
                borderRadius: 2
              }}
            >
              {t('notFound.backToHome')}
            </Button>
          </Box>

          {/* Quick Actions */}
          <Box mb={6}>
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}
            >
              {t('notFound.whatCanYouDo')}
            </Typography>
            
            <Grid container spacing={4}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}
                    onClick={action.action}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box mb={2}>
                        {action.icon}
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {action.description}
                      </Typography>
                      <Button
                        variant="outlined"
                        color={action.color}
                        size="small"
                        sx={{ borderRadius: 2 }}
                      >
                        {t('common.goTo')}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Popular Links */}
          <Paper elevation={0} sx={{ p: 4, border: '1px solid #e0e0e0', textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              {t('notFound.popularLinks')}
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2, 
                justifyContent: 'center',
                '& > *': {
                  minWidth: { xs: '100%', sm: 'auto' }
                }
              }}
            >
              <Button 
                variant="text" 
                onClick={() => navigate('/features')}
                sx={{ textTransform: 'none' }}
              >
                {t('navigation.features')}
              </Button>
              <Button 
                variant="text" 
                onClick={() => navigate('/pricing')}
                sx={{ textTransform: 'none' }}
              >
                {t('navigation.pricing')}
              </Button>
              <Button 
                variant="text" 
                onClick={() => navigate('/about')}
                sx={{ textTransform: 'none' }}
              >
                {t('navigation.about')}
              </Button>
              <Button 
                variant="text" 
                onClick={() => navigate('/posts')}
                sx={{ textTransform: 'none' }}
              >
                {t('navigation.blog')}
              </Button>
              <Button 
                variant="text" 
                onClick={() => navigate('/auth')}
                sx={{ textTransform: 'none' }}
              >
                {t('navigation.signUp')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default NotFound;